// Die paar Bausteine, die sich Anmelde-, Warte- und Verwaltungsseite teilen.
//
// Bewusst klein gehalten: Der eigentliche Bildschirm der App kommt in Phase 3
// und wird gegen die Referenz-Screenshots gebaut. Was hier steht, sind
// Flaechen, die es in der Original-App nicht gibt (Projektplan 7.6), also
// frei gestaltet, aber in derselben Material-Anmutung.

import type { ReactNode } from "react";

export function Rahmen({ titel, kinder }: { titel?: string; kinder: ReactNode }) {
  return (
    <div className="min-h-full font-sans text-neutral-900">
      <header className="bg-akzent px-4 py-3 text-white shadow">
        <h1 className="text-lg font-medium">{titel ?? "Kennzeichensammler"}</h1>
      </header>
      <main className="mx-auto max-w-md p-4">{kinder}</main>
    </div>
  );
}

export function Karte({ kinder }: { kinder: ReactNode }) {
  return <section className="mb-4 rounded bg-flaeche-inhalt p-4 shadow-sm">{kinder}</section>;
}

export function Knopf({
  kinder,
  onClick,
  art = "haupt",
  disabled,
  type = "button",
}: {
  kinder: ReactNode;
  onClick?: () => void;
  art?: "haupt" | "neben" | "warnung";
  disabled?: boolean;
  type?: "button" | "submit";
}) {
  const farben =
    art === "haupt"
      ? "bg-akzent text-white hover:bg-akzent-dunkel"
      : art === "warnung"
        ? "border border-red-300 text-red-700 hover:bg-red-50"
        : "border border-neutral-300 text-neutral-700 hover:bg-neutral-50";
  return (
    <button
      type={type}
      onClick={onClick}
      disabled={disabled}
      // 44 Pixel Mindesthoehe: Alles darunter trifft man auf dem Telefon nicht
      // zuverlaessig.
      className={`min-h-11 rounded px-4 py-2 text-sm font-medium transition disabled:opacity-50 ${farben}`}
    >
      {kinder}
    </button>
  );
}

/**
 * Kippschalter der Einstellungen-Seite (docs/reference/einstellungen-
 * oben.png/-unten.png, 02.09.2026): grauer Balken/weisser Punkt aus, blauer
 * Balken/weisser Punkt rechts an - anders als die freien Bausteine oben
 * kommt dieser aus dem Original, deshalb per Pipette statt frei gestaltet.
 */
export function Schalter({
  an,
  onAendern,
  label,
}: {
  an: boolean;
  onAendern: (naechsterWert: boolean) => void;
  label: string;
}) {
  return (
    <button
      type="button"
      role="switch"
      aria-checked={an}
      aria-label={label}
      onClick={() => onAendern(!an)}
      className={`relative h-8 w-14 shrink-0 rounded-full transition-colors ${an ? "bg-akzent" : "bg-neutral-300"}`}
    >
      <span
        className={`absolute top-1 h-6 w-6 rounded-full bg-white shadow transition-transform ${an ? "translate-x-7" : "translate-x-1"}`}
      />
    </button>
  );
}

/**
 * Das "?" neben vielen Zeilen der Einstellungen-Seite. Im Original oeffnet
 * ein Tipp darauf vermutlich einen Erklaertext (nicht fotografiert, welchen
 * Wortlaut oder welche Darstellung) - hier deshalb nur die Optik uebernommen,
 * ohne Tipp-Verhalten, statt einen Text zu erfinden, den kein Screenshot
 * belegt.
 */
export function HilfeIcon() {
  return (
    <span
      aria-hidden="true"
      className="inline-flex h-5 w-5 items-center justify-center rounded-full bg-neutral-400 text-xs text-white"
    >
      ?
    </span>
  );
}

export function Hinweis({ art, kinder }: { art: "fehler" | "info" | "gut"; kinder: ReactNode }) {
  const farben =
    art === "fehler"
      ? "border-red-200 bg-red-50 text-red-800"
      : art === "gut"
        ? "border-green-200 bg-green-50 text-green-800"
        : "border-blue-200 bg-blue-50 text-blue-900";
  return <p className={`mb-3 rounded border px-3 py-2 text-sm ${farben}`}>{kinder}</p>;
}
